import { useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Globe,
  Smartphone,
  Palette,
  Cloud,
  Zap,
  Shield,
  Code2,
} from "lucide-react";
import {
  SiReact,
  SiNextdotjs,
  SiNodedotjs,
  SiTypescript,
  SiPostgresql,
  SiRedis,
  SiDocker,
  SiFirebase,
  SiFigma,
  SiKubernetes,
  SiPython,
  SiApachekafka,
  SiElasticsearch,
  SiSwift,
  SiFlutter,
  SiGooglecloud,
  SiTerraform,
  SiPrometheus,
  SiGrafana,
  SiTailwindcss,
  SiBurpsuite,
  SiSplunk,
  SiGraphql,
  SiKotlin,
  SiSnyk,
  SiZapier,
  SiN8N,
  SiTensorflow,
  SiOpenai,
  SiLottiefiles,
  SiStorybook,
  SiHotjar,
  SiMaze,
  SiMqtt,
  SiInfluxdb,
  SiRedux,
  SiArgo,
  SiOwasp,
  SiMetasploit,
  SiWireshark,
  SiGoland,
} from "react-icons/si";
import AnimateOnScroll, {
  StaggerContainer,
  StaggerItem,
} from "@/components/AnimateOnScroll";
import ServiceBackground from "@/components/ServiceBackground";
import { services } from "@/lib/data";
import { useTranslation } from "react-i18next";

const iconMap: Record<string, any> = { Globe, Smartphone, Palette, Cloud, Zap, Shield };

const techIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  React: SiReact,
  "Next.js": SiNextdotjs,
  "Node.js": SiNodedotjs,
  TypeScript: SiTypescript,
  PostgreSQL: SiPostgresql,
  Redis: SiRedis,
  Docker: SiDocker,
  Firebase: SiFirebase,
  Figma: SiFigma,
  Kubernetes: SiKubernetes,
  Python: SiPython,
  "Apache Kafka": SiApachekafka,
  Kafka: SiApachekafka,
  Elasticsearch: SiElasticsearch,
  Swift: SiSwift,
  Flutter: SiFlutter,
  "Google Cloud": SiGooglecloud,
  GCP: SiGooglecloud,
  Terraform: SiTerraform,
  Prometheus: SiPrometheus,
  Grafana: SiGrafana,
  Tailwind: SiTailwindcss,
  "Burp Suite": SiBurpsuite,
  Splunk: SiSplunk,
  GraphQL: SiGraphql,
  Kotlin: SiKotlin,
  Snyk: SiSnyk,
  Zapier: SiZapier,
  n8n: SiN8N,
  TensorFlow: SiTensorflow,
  OpenAI: SiOpenai,
  Lottie: SiLottiefiles,
  Storybook: SiStorybook,
  Hotjar: SiHotjar,
  Maze: SiMaze,
  MQTT: SiMqtt,
  InfluxDB: SiInfluxdb,
  Redux: SiRedux,
  ArgoCD: SiArgo,
  "OWASP ZAP": SiOwasp,
  Metasploit: SiMetasploit,
  Wireshark: SiWireshark,
  Go: SiGoland,
  "React Native": SiReact,
};

export default function LayananDetailPage() {
  const params = useParams();
  const { t } = useTranslation();
  
  const originalService = services.find((s) => s.slug === params.slug);
  const translatedServices = (t('home.layanan_services', { returnObjects: true }) || []) as any[];
  const translatedService = translatedServices.find((s: any) => s.slug === params.slug);
  
  // Merge so features/benefits won't crash if they aren't fully translated yet
  const service = originalService ? {
    ...originalService,
    ...translatedService,
    subServices: originalService.subServices.map((sub, idx) => ({
      ...sub,
      ...(translatedService?.subServices?.[idx] || {})
    }))
  } : null;

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold gradient-text-white mb-4">Layanan Tidak Ditemukan</h1>
          <Link to="/layanan" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Layanan
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon];

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        {/* Service-specific animated background */}
        <ServiceBackground slug={params.slug as string} />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 via-transparent to-navy-950" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-neon-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 left-0 w-[400px] h-[400px] bg-neon-400/5 rounded-full blur-[100px]" />

        <div className="section-container section-padding relative z-10">
          <AnimateOnScroll>
            <Link
              to="/layanan"
              className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-neon-400 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('home.layanan_detail.semua_layanan')}
            </Link>

            <div className="flex items-start gap-6 mb-6 p-6 rounded-2xl bg-white/[0.02] backdrop-blur-sm border border-white/[0.06]">
              <div className="w-16 h-16 rounded-2xl bg-neon-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-neon-500/20 transition-colors">
                {Icon && <Icon className="w-8 h-8 text-neon-400" />}
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold gradient-text-white mb-3">
                  {service.title}
                </h1>
                <p className="text-neon-400/80 text-lg font-medium">{service.tagline}</p>
              </div>
            </div>

            <p className="text-white/50 text-lg leading-relaxed max-w-3xl bg-navy-950/30 p-4 rounded-xl backdrop-blur-sm">
              {service.description}
            </p>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="section-container section-padding">
          <AnimateOnScroll className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold gradient-text-white">
              {t('home.layanan_detail.apa_yang_kami_tawarkan')}
            </h2>
          </AnimateOnScroll>
          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" staggerDelay={0.08}>
            {service.features.map((feature: string) => (
              <StaggerItem key={feature}>
                <div className="glass-card p-5 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-neon-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white/70">{feature}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Sub Services */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-500/[0.02] to-transparent" />
        <div className="section-container section-padding relative z-10">
          <AnimateOnScroll className="mb-12">
            <span className="text-neon-400 font-mono text-sm tracking-widest uppercase">
              {t('home.layanan_detail.sub_layanan')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold gradient-text-white mt-2">
              {t('home.layanan_detail.layanan_detail')}
            </h2>
          </AnimateOnScroll>

          <StaggerContainer className="space-y-6" staggerDelay={0.12}>
            {service.subServices.map((sub: any, index: number) => (
              <StaggerItem key={sub.title}>
                <div className="glass-card-hover p-8">
                  <div className="grid md:grid-cols-[1fr_1.5fr] gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-3xl font-bold gradient-text font-mono">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <h3 className="text-xl font-semibold text-white">{sub.title}</h3>
                      </div>
                      <p className="text-white/40 text-sm leading-relaxed">
                        {sub.description}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider">
                        {t('home.layanan_detail.fitur_utama')}
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {sub.features.map((f: string) => (
                          <div key={f} className="flex items-center gap-2 text-sm text-white/50">
                            <CheckCircle2 className="w-4 h-4 text-neon-500/50 flex-shrink-0" />
                            {f}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="section-container section-padding">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll variant="slideRight">
              <span className="text-neon-400 font-mono text-sm tracking-widest uppercase">
                {t('home.layanan_detail.keunggulan')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold gradient-text-white mt-2 mb-6">
                {t('home.layanan_detail.mengapa_memilih', { service: service.shortTitle || service.title })}
              </h2>
              <div className="space-y-4">
                {service.benefits.map((benefit: string) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-neon-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-neon-400" />
                    </div>
                    <span className="text-white/60">{benefit}</span>
                  </div>
                ))}
              </div>
            </AnimateOnScroll>

            <AnimateOnScroll variant="slideLeft">
              <div className="glass-card p-8">
                <h3 className="text-lg font-semibold text-white mb-4">{t('home.layanan_detail.tech_stack')}</h3>
                <div className="flex flex-wrap gap-3">
                  {service.techStack.map((tech: string) => {
                    const TechIcon = techIconMap[tech];
                    if (TechIcon) {
                      return (
                        <div
                          key={tech}
                          className="p-2 bg-neon-500/10 rounded-lg flex items-center justify-center"
                        >
                          <TechIcon className="w-6 h-6 text-neon-400" />
                        </div>
                      );
                    }
                    return (
                      <div
                        key={tech}
                        className="px-4 py-2 bg-neon-500/10 text-neon-400 rounded-lg text-sm font-mono font-medium"
                      >
                        <Code2 className="w-5 h-5 inline mr-1" />
                        {tech}
                      </div>
                    );
                  })}
                </div>
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32 pt-10 relative z-10">
        <div className="section-container section-padding">
          <AnimateOnScroll>
            <div className="glass-card p-10 md:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-neon-500/5 to-transparent" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-bold gradient-text-white mb-4">
                  {t('home.layanan_detail.cta_title', { service: service.shortTitle || service.title })}
                </h2>
                <p className="text-white/50 max-w-xl mx-auto mb-8">
                  {t('home.layanan_detail.cta_desc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/konsultasi" className="btn-primary">
                    {t('home.layanan_detail.btn_jadwal')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/studi-kasus" className="btn-secondary">
                    {t('home.layanan_detail.btn_studi_kasus')}
                  </Link>
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </>
  );
}
